import { React } from "react";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import FormGroup from "react-bootstrap/FormGroup";
import StringUtils from "../../utils/StringUtils";
import ActionButton from "../common/widgets/ActionButton";
import FormText from "react-bootstrap/FormText";
import useTranslationAuthenticationUi from "./useTranslationAuthenticationUi";
import useShowLoader from "../common/loading-widgets/useShowLoader";
import useAuthUser from "../authentication-no-ui/useAuthUser";
import useSignUp from "../authentication-no-ui/useSignUp";
import * as ROUTES from "../../constants/routes";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import useReferrer from "../infra-no-ui/navigation/useReferrer";
import SignInWithAuthProvider from "./SignInWithAuthProvider";
import ActionLink from "../common/widgets/ActionLink";
import Separator from "../common/widgets/Separator";
import ValidatedInputText from "../common/widgets/ValidatedInputText";
import ValidatedInputEmail from "../common/widgets/ValidatedInputEmail";
import { useValidatedInputForm } from "../common/widgets/useValidatedInputForm";
import { MIN_LENGTH_PASSWORD } from "../../constants/ui";
import Terms from "../footer/Terms";
import CloseIcon from "@material-ui/icons//Close";
import useGenericModal from "../common/modals/useGenericModal";
import "./SignUpForm.scss";

export default function SignUpForm() {
  const { signUp, loading, error, ready: signUpReady } = useSignUp();
  const { t, loading: tLoading } = useTranslationAuthenticationUi();
  const authUser = useAuthUser();
  const { referrer } = useReferrer(ROUTES.RECRUITER_DASHBOARD);
  const location = useLocation();
  const history = useHistory();

  useShowLoader(tLoading || !signUpReady, "SignUpForm");

  // If we tried to access a private page without being authenticated, signing up will redirect
  // to referrer. Keep the referrer if user has an account and does not need to sign up, just sign in
  const onClickSignIn = (event) => {
    event.preventDefault();
    const signInLocationWithReferrer = {
      pathname: ROUTES.SIGN_IN,
      state: location.state,
    };

    history.push(signInLocationWithReferrer);
  };

  const {
    control,
    formState: { isValid },
    getValues,
  } = useValidatedInputForm();

  const onSubmit = (event) => {
    event.preventDefault();
    const fields = getValues();
    signUp(fields.email.trim(), fields.password);
  };

  const canSubmit = () => {
    return isValid;
  };

  // Modal to show Terms details form
  const {
    GenericModal: termsModal,
    show: showTermsModal,
    hide: hideTermsModal,
  } = useGenericModal("termsModal");

  const onClickTerms = () => {
    showTermsModal(getTerms());
  };

  const getTerms = () => {
    return (
      <div className="terms-modal">
        <Row className="close-icon">
          <Col>
            <CloseIcon onClick={hideTermsModal} className="close-icon" />
          </Col>
        </Row>
        <Row>
          <Col>
            <Row>
              <Col>
                <h1>{t("authentication-ui:terms_and_conditions")}</h1>
              </Col>
            </Row>
            <Row>
              <Col>
                <Terms onClose={hideTermsModal} />
              </Col>
            </Row>
          </Col>
        </Row>
        <Row>
          <Col className="action-close">
            <ActionButton onClick={hideTermsModal}>
              {t("authentication-ui:close_button")}
            </ActionButton>
          </Col>
        </Row>
      </div>
    );
  };

  // RENDER

  // If already logged, or when rendering after sign up, do not display this page, redirect to referrer or HOME
  if (authUser.authenticated) {
    return <Redirect to={referrer} />;
  }

  function renderForm() {
    // For some reason that is internal to Firebase, StyledFirebaseAuth rendered by SignInWithAuthProvider does not re-render
    // when the button labels change, so make sure the form is not displayed until the translation is ready.
    // A more robust solution needs to be found because labels still don't change when language changes
    if (tLoading) return null;

    return (
      <>
        {termsModal}
        <div className={"SignUpForm"}>
          <div>
            <Row>
              <Col>
                <SignInWithAuthProvider
                  googleButtonLabel={t("authentication-ui:sign_up_with_google")}
                  facebookButtonLabel={t(
                    "authentication-ui:sign_up_with_facebook"
                  )}
                />
              </Col>
            </Row>
          </div>
          <Row>
            <Col className="separator-col">
              <Separator>
                {t("authentication-ui:sign_up_with_email_msg")}
              </Separator>
            </Col>
          </Row>
          <Row>
            <Col className="input-col-container">
              <Form onSubmit={onSubmit} className="form-with-rows">
                <FormGroup controlId="email">
                  <ValidatedInputEmail
                    required={true}
                    name="email"
                    control={control}
                    className="FormControl"
                    autoFocus
                    type="email"
                    placeholder={t(
                      "authentication-ui:sign_up_email_placeholder"
                    )}
                    autoComplete="email"
                  />
                </FormGroup>
                <FormGroup controlId="password">
                  <ValidatedInputText
                    minLength={MIN_LENGTH_PASSWORD}
                    control={control}
                    required={true}
                    className="FormControl"
                    name="password"
                    type="password"
                    placeholder={t(
                      "authentication-ui:sign_up_password_placeholder"
                    )}
                    autoComplete="current-password"
                  />
                </FormGroup>
                <FormGroup>
                  <Form.Row className="FormRow">
                    <ActionButton
                      loading={loading}
                      type="submit"
                      disabled={!canSubmit()}
                    >
                      {t("authentication-ui:sign_up_sign_up_button")}
                    </ActionButton>
                  </Form.Row>
                </FormGroup>
                {!StringUtils.isNullOrEmpty(error) && (
                  <FormText as="div" className="form-error">
                    {error}
                  </FormText>
                )}
              </Form>
            </Col>
          </Row>
          <Row>
            <Col>
              {t("authentication-ui:terms_and_conditions_agree_msg")}
              <ActionLink onClick={onClickTerms}>
                {t("authentication-ui:terms_and_conditions")}
              </ActionLink>
            </Col>
          </Row>
          <Row>
            <Col>
              {t("authentication-ui:sign_up_sign_in_question")}
              <ActionLink className="sign-in-action" onClick={onClickSignIn}>
                {t("authentication-ui:sign_up_sign_in_link")}
              </ActionLink>
            </Col>
          </Row>
        </div>
      </>
    );
  }

  return <div className="Signup">{renderForm()}</div>;
}
